/**
 * EngageTrack Core Service
 * Manages all tracking functionality including WebSocket, sessions, and events
 */

import type {
  EngageTrackConfig,
  EventPayload,
  SessionState,
  WebSocketMessage,
  EventData,
} from '../types';
import { getCookie, setCookie, deleteCookie } from '../utils/cookies';
import { generateUUID } from '../utils/uuid';
import { getReferralData } from '../utils/referral';

const SDK_VERSION = '1.5.0';

/**
 * EngageTrack Service Class
 */
export class EngageTrackService {
  private config: EngageTrackConfig;
  private serverUrl: string;
  private wsUrl: string;

  // WebSocket
  private ws: WebSocket | null = null;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private reconnectAttempts = 0;
  private dbSessionId: string | null = null;

  // Session state
  private startTime: number = Date.now();
  private lastActivityTime: number = Date.now();
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private sessionEndSent = false;
  private pageViewCount = 0;
  private sessionTimeout: ReturnType<typeof setTimeout> | null = null;
  private pageHiddenSent = false;
  private navigationTimer: ReturnType<typeof setTimeout> | null = null;

  // Callbacks
  private onSessionEndCallback: (() => void) | null = null;

  constructor(config: EngageTrackConfig) {
    this.config = {
      serverUrl: 'https://api.engagetrack.net/api/track',
      idleTimeout: 30000,
      checkInterval: 1000,
      sessionTimeoutDuration: 30 * 60 * 1000,
      heartbeatIntervalMs: 30000,
      maxReconnectAttempts: 5,
      referralExpiryDays: 30,
      debug: false,
      disabled: false,
      ...config,
    };

    this.serverUrl = this.config.serverUrl!;
    this.wsUrl = this.serverUrl.replace('/api/track', '').replace('http', 'ws') + '/ws';

    // Check if disabled
    if (this.config.disabled) {
      this.log('Tracking is disabled');
      return;
    }

    // Check localStorage for disabled flag
    try {
      if (localStorage.getItem('engageTrackDisabled') === 'true') {
        this.log('Tracking is disabled via localStorage');
        this.config.disabled = true;
        return;
      }
    } catch (error) {
      this.log('localStorage access blocked:', error);
    }
  }

  /**
   * Initialize the tracking service
   */
  public initialize(): void {
    if (this.config.disabled) {
      return;
    }

    // Initialize session
    this.initializeSession();

    // Setup event listeners
    this.setupEventListeners();

    // Start activity monitoring
    this.startActivityMonitoring();

    // Connect WebSocket
    this.connectWebSocket();

    // Track initial page load
    this.trackPageLoad();
  }

  /**
   * Initialize session and get/create IDs
   */
  private initializeSession(): void {
    const sessionId = getCookie('session_id') || setCookie('session_id', generateUUID(), 1 / 48, this.config.domain);
    
    // Check if session already ended
    const sessionEndCookieName = `sessionEnd_${sessionId}`;
    if (getCookie(sessionEndCookieName)) {
      this.sessionEndSent = true;
    }

    // Track page views
    const pageViewCookieName = `pageViews_${sessionId}`;
    this.pageViewCount = parseInt(getCookie(pageViewCookieName) || '0') + 1;
    setCookie(pageViewCookieName, this.pageViewCount.toString(), 1 / 48, this.config.domain);

    // Ensure user ID exists
    getCookie('user_id') || setCookie('user_id', generateUUID(), 365, this.config.domain);

    this.startTime = Date.now();
    this.lastActivityTime = this.startTime;
  }

  /**
   * Track page load event
   */
  private trackPageLoad(): void {
    const referralData = getReferralData(this.config.referralExpiryDays);

    this.track('page_load', {
      url: window.location.href,
      title: document.title,
      timeSpent: 0,
      pageViewCount: this.pageViewCount,
      // Include referral information
      ...(referralData && {
        referralSource: referralData.source?.source || 'direct',
        referralDomain: referralData.source?.domain || null,
        referralUrl: referralData.source?.url || null,
        utmSource: referralData.urlParams?.utm_source || null,
        utmMedium: referralData.urlParams?.utm_medium || null,
        utmCampaign: referralData.urlParams?.utm_campaign || null,
        referralCode: referralData.urlParams?.ref || referralData.urlParams?.referral || null,
        landingPage: referralData.landingPage,
        referralTimestamp: referralData.timestamp,
      }),
    });
  }

  /**
   * Setup all event listeners
   */
  private setupEventListeners(): void {
    // Activity listeners
    document.addEventListener('mousemove', this.handleActivity);
    document.addEventListener('keydown', this.handleActivity);
    document.addEventListener('scroll', this.handleActivity);

    // Visibility change
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    // Click tracking
    document.addEventListener('click', this.handleClick);

    // Page lifecycle
    window.addEventListener('pagehide', this.handlePageHide);
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  }

  /**
   * Handle user activity
   */
  private handleActivity = (): void => {
    this.lastActivityTime = Date.now();
    this.resetSessionTimeout();
  };

  /**
   * Handle visibility change
   */
  private handleVisibilityChange = (): void => {
    if (document.hidden) {
      // Page hidden
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }

      if (!this.pageHiddenSent) {
        this.pageHiddenSent = true;
        this.track('page_hidden', {
          timeSpent: Date.now() - this.startTime,
        });
      }

      // Set timer for session end
      this.navigationTimer = setTimeout(() => {
        const finalTimeSpent = Date.now() - this.startTime;
        if (finalTimeSpent > 30000) {
          this.sendSessionEnd();
        }
      }, 5 * 60 * 1000);
    } else {
      // Page visible
      if (this.navigationTimer) {
        clearTimeout(this.navigationTimer);
        this.navigationTimer = null;
      }

      this.lastActivityTime = Date.now();
      this.startActivityMonitoring();
      this.pageHiddenSent = false;
    }
  };

  /**
   * Handle click events
   */
  private handleClick = (e: MouseEvent): void => {
    const target = (e.target as HTMLElement).closest('a') as HTMLAnchorElement;

    this.lastActivityTime = Date.now();
    this.resetSessionTimeout();

    if (!target || !target.href) {
      return;
    }

    const isMailto = target.href.startsWith('mailto:');
    const isPhone = target.href.startsWith('tel:');
    const hasTargetBlank = target.target === '_blank';

    if (!hasTargetBlank && !isMailto && !isPhone) {
      this.handleLinkClickedWithNavigation(target, e);
    } else {
      this.handleLinkClicked(target);
    }
  };

  /**
   * Handle page hide event
   */
  private handlePageHide = (): void => {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.stopHeartbeat();

    if (!this.pageHiddenSent) {
      this.pageHiddenSent = true;
      const finalTimeSpent = Date.now() - this.startTime;
      this.track('page_hidden', { timeSpent: finalTimeSpent });
    }

    setTimeout(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.close();
      }
    }, 100);
  };

  /**
   * Handle before unload event
   */
  private handleBeforeUnload = (): void => {
    const finalTimeSpent = Date.now() - this.startTime;

    if (finalTimeSpent > 3000) {
      this.navigationTimer = setTimeout(() => {
        this.sendSessionEnd();
      }, 100);
    }

    setTimeout(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.close();
      }
    }, 100);
  };

  /**
   * Start activity monitoring
   */
  private startActivityMonitoring(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }

    this.intervalId = setInterval(() => {
      this.checkActivity();
    }, this.config.checkInterval!);

    this.setSessionTimeout();
  }

  /**
   * Check activity for idle timeout
   */
  private checkActivity(): void {
    if (document.hidden) {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    } else {
      const currentTime = Date.now();
      if (currentTime - this.lastActivityTime >= this.config.idleTimeout!) {
        this.track('idle_timeout', {
          timeSpent: currentTime - this.startTime,
          idleDuration: currentTime - this.lastActivityTime,
        });
        this.lastActivityTime = currentTime;
      }
    }
  }

  /**
   * Set session timeout
   */
  private setSessionTimeout(): void {
    if (this.sessionTimeout) {
      clearTimeout(this.sessionTimeout);
    }

    this.sessionTimeout = setTimeout(() => {
      const finalTimeSpent = Date.now() - this.startTime;
      if (finalTimeSpent > 30000) {
        this.track('session_timeout', {
          timeSpent: finalTimeSpent,
          reason: 'prolonged_inactivity',
        });
        this.sendSessionEnd();
      }
    }, this.config.sessionTimeoutDuration!);
  }

  /**
   * Reset session timeout
   */
  private resetSessionTimeout(): void {
    this.setSessionTimeout();
  }

  /**
   * Handle link click
   */
  private handleLinkClicked(element: HTMLAnchorElement): void {
    if (!element || !element.href) {
      return;
    }

    const isExternalNavigation =
      element.hostname &&
      element.hostname !== window.location.hostname &&
      !element.href.startsWith('mailto:') &&
      !element.href.startsWith('tel:');

    const sessionId = getCookie('session_id');
    const userId = getCookie('user_id');

    const payload: EventPayload = {
      siteId: this.config.siteId,
      eventType: 'USER_CLICK',
      domain: this.config.domain,
      data: {
        sessionId: sessionId!,
        userId: userId!,
        userAgent: navigator.userAgent,
        path: window.location.pathname,
        referrer: document.referrer || null,
        title: document.title || null,
        url: element.href,
        element: element.textContent?.trim() || 'Link',
        timeSpent: Date.now() - this.startTime,
        isExternalNavigation: Boolean(isExternalNavigation),
      },
      timestamp: new Date().toISOString(),
      sdkVersion: SDK_VERSION,
    };

    // Use XMLHttpRequest for synchronous-like behavior
    const xhr = new XMLHttpRequest();
    xhr.open('POST', this.serverUrl, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.timeout = 10000;
    xhr.send(JSON.stringify(payload));

    xhr.onreadystatechange = () => {
      if (xhr.readyState === XMLHttpRequest.DONE) {
        if (isExternalNavigation) {
          const finalTimeSpent = Date.now() - this.startTime;
          if (finalTimeSpent > 3000) {
            this.sendSessionEnd();
          }
        }
      }
    };
  }

  /**
   * Handle link click with navigation
   */
  private handleLinkClickedWithNavigation(element: HTMLAnchorElement, event: MouseEvent): void {
    if (!element || !element.href) {
      return;
    }

    const isExternalLink =
      element.target === '_blank' ||
      element.href.startsWith('mailto:') ||
      element.href.startsWith('tel:') ||
      (element.hostname && element.hostname !== window.location.hostname);

    if (isExternalLink) {
      this.handleLinkClicked(element);
      return;
    }

    event.preventDefault();

    const sessionId = getCookie('session_id');
    const userId = getCookie('user_id');

    const payload: EventPayload = {
      siteId: this.config.siteId,
      eventType: 'USER_CLICK',
      domain: this.config.domain,
      data: {
        sessionId: sessionId!,
        userId: userId!,
        userAgent: navigator.userAgent,
        path: window.location.pathname,
        referrer: document.referrer || null,
        title: document.title || null,
        url: element.href,
        element: element.textContent?.trim() || 'Link',
        timeSpent: Date.now() - this.startTime,
      },
      timestamp: new Date().toISOString(),
      sdkVersion: SDK_VERSION,
    };

    const navigateToLink = () => {
      window.location.href = element.href;
    };

    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], {
        type: 'application/json',
      });
      const success = navigator.sendBeacon(this.serverUrl, blob);

      if (success) {
        navigateToLink();
      } else {
        this.handleWithFetch(payload, navigateToLink);
      }
    } else {
      this.handleWithFetch(payload, navigateToLink);
    }
  }

  /**
   * Handle with fetch fallback
   */
  private handleWithFetch(payload: EventPayload, navigateToLink: () => void): void {
    fetch(this.serverUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      keepalive: true,
    })
      .then(() => {
        navigateToLink();
      })
      .catch((error) => {
        this.log('Failed to track link click:', error);
        navigateToLink();
      });

    setTimeout(() => {
      navigateToLink();
    }, 1000);
  }

  /**
   * Connect WebSocket
   */
  private connectWebSocket(): void {
    try {
      this.ws = new WebSocket(this.wsUrl);

      this.ws.onopen = () => {
        this.reconnectAttempts = 0;
        this.sendHeartbeat();
        this.startHeartbeat();
      };

      this.ws.onmessage = (event) => {
        try {
          const data: WebSocketMessage = JSON.parse(event.data);
          this.handleWebSocketMessage(data);
        } catch (error) {
          this.log('Error parsing WebSocket message:', error);
        }
      };

      this.ws.onclose = () => {
        this.stopHeartbeat();
        this.scheduleReconnect();
      };

      this.ws.onerror = (error) => {
        this.log('WebSocket error:', error);
      };
    } catch (error) {
      this.log('Error creating WebSocket connection:', error);
      this.scheduleReconnect();
    }
  }

  /**
   * Handle WebSocket message
   */
  private handleWebSocketMessage(data: WebSocketMessage): void {
    switch (data.type) {
      case 'ack_connection':
        this.log('WebSocket connection established with server');
        break;
      case 'heartbeat_ack':
        this.log('Heartbeat acknowledged by server');
        if (data.dbSessionId) {
          this.dbSessionId = data.dbSessionId;
          this.log('Database session ID received:', this.dbSessionId);
        }
        break;
      case 'session_ended':
        this.log('Server indicates session has ended, generating new session');
        this.handleSessionEndedByServer(data);
        break;
      default:
        this.log('Unknown WebSocket message type:', data.type);
        break;
    }
  }

  /**
   * Send heartbeat
   */
  private sendHeartbeat(): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const sessionId = getCookie('session_id');
      const userId = getCookie('user_id');

      if (sessionId && userId) {
        const heartbeatMessage: WebSocketMessage = {
          type: 'heartbeat',
          siteId: this.config.siteId,
          sessionId: sessionId,
          userId: userId,
          timestamp: new Date().toISOString(),
        };

        if (this.dbSessionId) {
          heartbeatMessage.dbSessionId = this.dbSessionId;
        }

        this.ws.send(JSON.stringify(heartbeatMessage));
      }
    }
  }

  /**
   * Start heartbeat
   */
  private startHeartbeat(): void {
    this.stopHeartbeat();

    this.heartbeatInterval = setInterval(() => {
      this.sendHeartbeat();
    }, this.config.heartbeatIntervalMs!);
  }

  /**
   * Stop heartbeat
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Handle session ended by server
   */
  private handleSessionEndedByServer(data: WebSocketMessage): void {
    this.log('Handling session ended by server:', data.sessionId);

    const oldSessionId = data.clientSessionId || data.sessionId;

    deleteCookie('session_id', this.config.domain);
    this.dbSessionId = null;

    const newSessionId = generateUUID();
    setCookie('session_id', newSessionId, 1 / 48, this.config.domain);

    this.startTime = Date.now();
    this.lastActivityTime = this.startTime;
    this.sessionEndSent = false;
    this.pageViewCount = 1;
    this.pageHiddenSent = false;

    if (oldSessionId) {
      deleteCookie(`sessionEnd_${oldSessionId}`, this.config.domain);
      deleteCookie(`pageViews_${oldSessionId}`, this.config.domain);
    }

    const pageViewCookieName = `pageViews_${newSessionId}`;
    setCookie(pageViewCookieName, this.pageViewCount.toString(), 1 / 48, this.config.domain);

    setTimeout(() => {
      this.sendHeartbeat();
    }, 100);

    this.log(`New session started: ${newSessionId} (previous: ${oldSessionId})`);
  }

  /**
   * Schedule reconnect
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts < this.config.maxReconnectAttempts!) {
      this.reconnectAttempts++;
      const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);

      this.log(`Scheduling WebSocket reconnect attempt ${this.reconnectAttempts} in ${delay}ms`);

      setTimeout(() => {
        this.connectWebSocket();
      }, delay);
    } else {
      this.log('Max WebSocket reconnection attempts reached');
    }
  }

  /**
   * Send session end
   */
  private sendSessionEnd(): void {
    const sessionId = getCookie('session_id');
    const userId = getCookie('user_id');

    if (this.navigationTimer) {
      clearTimeout(this.navigationTimer);
      this.navigationTimer = null;
    }

    const timeSpent = Date.now() - this.startTime;

    if (sessionId && userId && timeSpent > 1000) {
      // Send via WebSocket
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(
          JSON.stringify({
            type: 'session_end',
            siteId: this.config.siteId,
            sessionId: sessionId,
            userId: userId,
            timeSpent: Math.floor(timeSpent / 1000),
            timestamp: new Date().toISOString(),
          })
        );
      }

      // Send via HTTP
      this.track('session_end', {
        timeSpent: timeSpent,
        sessionDuration: Math.floor(timeSpent / 1000),
        pageViewCount: this.pageViewCount,
      });

      deleteCookie('session_id', this.config.domain);
      deleteCookie(`sessionEnd_${sessionId}`, this.config.domain);
      deleteCookie(`pageViews_${sessionId}`, this.config.domain);

      this.log(`Session ended: ${sessionId}, duration: ${Math.floor(timeSpent / 1000)}s`);

      if (this.onSessionEndCallback) {
        this.onSessionEndCallback();
      }
    }
  }

  /**
   * Track event
   */
  public track(eventType: string, data: Record<string, unknown> = {}): void {
    if (this.config.disabled) {
      return;
    }

    const sessionId = getCookie('session_id') || setCookie('session_id', generateUUID(), 1 / 48, this.config.domain);
    const userId = getCookie('user_id') || setCookie('user_id', generateUUID(), 365, this.config.domain);

    const referralData = getReferralData(this.config.referralExpiryDays);

    const payload: EventPayload = {
      siteId: this.config.siteId,
      eventType: eventType.toUpperCase(),
      domain: this.config.domain,
      data: {
        ...data,
        sessionId,
        userId,
        userAgent: navigator.userAgent,
        path: window.location.pathname,
        referrer: document.referrer || null,
        title: document.title || null,
        referral: referralData,
      } as EventData,
      timestamp: new Date().toISOString(),
      sdkVersion: SDK_VERSION,
    };

    fetch(this.serverUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }).catch((error) => {
      this.log('Error sending event:', error);
    });
  }

  /**
   * Track referral conversion
   */
  public trackReferralConversion(conversionData: Record<string, unknown> = {}): void {
    const referralData = getReferralData(this.config.referralExpiryDays);

    this.track('referral_conversion_manual', {
      ...conversionData,
      referralData: referralData,
    });
  }

  /**
   * Get session state
   */
  public getSessionState(): SessionState {
    return {
      sessionId: getCookie('session_id') || null,
      userId: getCookie('user_id') || null,
      dbSessionId: this.dbSessionId,
      startTime: this.startTime,
      lastActivityTime: this.lastActivityTime,
      pageViewCount: this.pageViewCount,
      isActive: !this.sessionEndSent,
      isConnected: this.ws?.readyState === WebSocket.OPEN,
    };
  }

  /**
   * Set session end callback
   */
  public onSessionEnd(callback: () => void): void {
    this.onSessionEndCallback = callback;
  }

  /**
   * Cleanup
   */
  public cleanup(): void {
    // Clear intervals and timeouts
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    if (this.sessionTimeout) {
      clearTimeout(this.sessionTimeout);
    }
    if (this.navigationTimer) {
      clearTimeout(this.navigationTimer);
    }

    // Stop heartbeat
    this.stopHeartbeat();

    // Close WebSocket
    if (this.ws) {
      this.ws.close();
    }

    // Remove event listeners
    document.removeEventListener('mousemove', this.handleActivity);
    document.removeEventListener('keydown', this.handleActivity);
    document.removeEventListener('scroll', this.handleActivity);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    document.removeEventListener('click', this.handleClick);
    window.removeEventListener('pagehide', this.handlePageHide);
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  }

  /**
   * Debug logging
   */
  private log(...args: unknown[]): void {
    if (this.config.debug) {
      console.log('[EngageTrack]', ...args);
    }
  }
}
